<template>
  <div>
    <div class="mb-2 flex items-center space-x-2">
      <input
        v-model="localEnabled"
        type="checkbox"
        class="text-primary focus:ring-primary border-background-fg/20 h-4 w-4 rounded"
        :disabled="disabled"
      />
      <label
        class="text-background-fg/87 cursor-pointer text-xs font-semibold"
        @click="toggleEnabled"
      >
        {{ label }}
      </label>
    </div>

    <div class="flex space-x-2" :class="{ 'opacity-30': !localEnabled }">
      <BaseInputSelect
        v-model="selectedHour"
        :options="hourOptions"
        input-class="text-xs"
        :disabled="disabled || !localEnabled"
        label=""
      />
      <span class="text-background-fg/70 flex items-center">:</span>
      <BaseInputSelect
        v-model="selectedMinute"
        :options="minuteOptions"
        input-class="text-xs"
        :disabled="disabled || !localEnabled"
        label=""
      />
    </div>
  </div>
</template>

<script setup lang="ts">
const props = defineProps<{
  modelValue: string | null | undefined;
  enabled: boolean;
  label?: string;
  disabled?: boolean;
  start?: string | null | undefined;
}>();

const emit = defineEmits<{
  (event: "update:modelValue", value: typeof props.modelValue): void;
  (event: "update:enabled", value: boolean): void;
}>();

// Generate hour options based on start time (24 hours from start)
const hourOptions = computed(() => {
  if (!props.start) {
    // Default behavior: all hours 00-23
    return Array.from({ length: 24 }, (_, i) => ({
      value: String(i).padStart(2, "0"),
      label: String(i).padStart(2, "0"),
    }));
  }

  const startHour = parseInt(props.start.split(":")[0]);
  const options = [];

  // Show 24 hours starting from startHour
  for (let i = 0; i < 24; i++) {
    const hour = (startHour + i) % 24;
    const hourStr = String(hour).padStart(2, "0");
    const isNextDay = startHour + i >= 24;

    options.push({
      value: hourStr,
      label: isNextDay ? `+${hourStr}` : hourStr,
    });
  }

  return options;
});

// Generate minute options (00, 15, 30, 45)
const minuteOptions = computed(() => [
  { value: "00", label: "00" },
  { value: "15", label: "15" },
  { value: "30", label: "30" },
  { value: "45", label: "45" },
]);

// Parse the time value to get hour and minute
const parseTime = (time: string | null | undefined) => {
  if (!time) return { hour: "00", minute: "00" };
  const [hour, minute] = time.split(":");
  return {
    hour: hour || "00",
    minute: minute || "00",
  };
};

// Local enabled state that syncs with the prop
const localEnabled = computed({
  get: () => props.enabled,
  set: (value) => emit("update:enabled", value),
});

// Initialize hour and minute from modelValue
const { hour: initialHour, minute: initialMinute } = parseTime(
  props.modelValue,
);
const selectedHour = ref(initialHour);
const selectedMinute = ref(initialMinute);

// Watch for changes in hour and minute
watch([selectedHour, selectedMinute], ([hour, minute]) => {
  if (localEnabled.value) {
    const timeValue = `${hour}:${minute}`;
    emit("update:modelValue", timeValue);
  }
});

// Watch for external modelValue changes
watch(
  () => props.modelValue,
  (newValue) => {
    if (newValue) {
      const { hour, minute } = parseTime(newValue);
      selectedHour.value = hour;
      selectedMinute.value = minute;
    }
  },
);

// Add toggle function
const toggleEnabled = () => {
  if (!props.disabled) {
    emit("update:enabled", !props.enabled);
  }
};
</script>
